import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Api, { getApiUrl } from '../../services/api';
import MessageModal from '../common/MessageModal';
import { useAuth } from '../../context/AuthContext';

const Profile = ({ user, onLogout: _onLogout }) => {
  const { updateUser } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    totalSpent: 0
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [editingField, setEditingField] = useState(null); // 'username', 'email', 'phone', 'pincode', 'address', null
  const [editFormData, setEditFormData] = useState({ username: '', email: '', phone: '', pincode: '', address: '' });
  const [updating, setUpdating] = useState(false);
  const [messageModal, setMessageModal] = useState({ show: false, message: '', type: 'info', title: 'Message' });

  // Debug: Log messageModal changes
  useEffect(() => {
    console.log('📢 messageModal state changed:', messageModal);
  }, [messageModal]);

  // Auto-hide toast message after 2.5 seconds (non-blocking, no user interaction required)
  useEffect(() => {
    if (!messageModal.show) return undefined;
    console.log('⏰ Starting auto-hide timer for toast...');
    const timeoutId = setTimeout(() => {
      console.log('⏰ Auto-hiding toast after 2.5s');
      setMessageModal(prev => ({ ...prev, show: false }));
    }, 2500); // 2.5 seconds - perfect balance
    return () => {
      console.log('🧹 Cleaning up toast timer');
      clearTimeout(timeoutId);
    };
  }, [messageModal.show]);

  useEffect(() => {
    fetchUserRides();
  }, [user]);

  const fetchUserRides = async () => {
    try {
      const token = localStorage.getItem('uttaapp_token');
      const response = await axios.get(Api.Booking.myBookings, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.bookings) {
        const bookings = response.data.bookings;
        
        // Calculate statistics
        const total = bookings.length;
        const completed = bookings.filter(b => b.status === 'completed' || b.status === 'accepted').length;
        const pending = bookings.filter(b => b.status === 'pending' || b.status === 'negotiating').length;
        const totalSpent = bookings
          .filter(b => b.fare && (b.status === 'completed' || b.paymentStatus === 'completed'))
          .reduce((sum, b) => sum + (parseFloat(b.fare) || 0), 0);
        
        setStats({ total, completed, pending, totalSpent });
      }
    } catch (error) {
      console.error('Error fetching user rides:', error);
    }
  };

  const getProfileImageUrl = (profilePicture) => {
    if (!profilePicture) return null;
    // If it's a full URL, return as is
    if (profilePicture.startsWith('http')) return profilePicture;
    // Otherwise, construct backend URL using centralized API helper
    const baseUrl = getApiUrl();
    return `${baseUrl}/${profilePicture.replace(/\\/g, '/')}`;
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessageModal({ show: true, message: 'Please select an image file', type: 'error', title: 'Invalid File' });
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload profile picture
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const token = localStorage.getItem('uttaapp_token');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      console.log('📤 Uploading profile picture to:', Api.User.updateProfilePicture);
      console.log('📦 File details:', { name: file.name, type: file.type, size: file.size });
      
      const response = await axios.put(Api.User.updateProfilePicture, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('📥 Profile picture upload response:', response.data);

      if (response.data.success) {
        // Get the updated profile picture from response
        const updatedProfilePicture = response.data.profilePicture || response.data.user?.profilePicture || response.data.data?.profilePicture;
        
        if (!updatedProfilePicture) {
          console.warn('⚠️ Profile picture updated but no profilePicture in response:', response.data);
          throw new Error('Profile picture updated but server did not return the new picture path');
        }

        // Update user context with new profile picture
        const updatedUser = { ...user, profilePicture: updatedProfilePicture };
        
        if (updateUser) {
          updateUser(updatedUser);
          console.log('✅ User context updated with new profile picture:', updatedProfilePicture);
        } else {
          console.warn('⚠️ updateUser function not available, updating localStorage directly');
          // Fallback: Update localStorage directly if updateUser is not available
          localStorage.setItem('uttaapp_user', JSON.stringify(updatedUser));
        }
        
        // Also ensure localStorage is updated (double-check)
        try {
          const storedUser = localStorage.getItem('uttaapp_user');
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            parsedUser.profilePicture = updatedProfilePicture;
            localStorage.setItem('uttaapp_user', JSON.stringify(parsedUser));
            console.log('✅ localStorage also updated with new profile picture');
          }
        } catch (localStorageError) {
          console.error('❌ Error updating localStorage:', localStorageError);
        }
        
        // Show non-blocking toast notification
        console.log('✅ Profile picture updated successfully in database!');
        const toastData = { 
          show: true, 
          message: 'Profile picture updated successfully!', 
          type: 'success', 
          title: '' // No title for cleaner look
        };
        setMessageModal(toastData);
        setProfilePicturePreview(null);
      } else {
        throw new Error(response.data.message || 'Failed to update profile picture');
      }
    } catch (error) {
      console.error('Error updating profile picture:', error);
      setMessageModal({ show: true, message: error.response?.data?.message || 'Failed to update profile picture. Please try again.', type: 'error', title: 'Error' });
      setProfilePicturePreview(null);
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle edit field
  const handleEditClick = (field) => {
    setEditingField(field);
    setEditFormData({
      username: user?.username || '',
      email: user?.email || '',
      phone: user?.phone || '',
      pincode: user?.pincode || '',
      address: user?.address || ''
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingField(null);
    setEditFormData({ username: '', email: '', phone: '', pincode: '', address: '' });
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('uttaapp_token');
      const updateData = {};
      
      if (editingField === 'username' && editFormData.username) {
        updateData.username = editFormData.username;
      } else if (editingField === 'pincode' && editFormData.pincode) {
        updateData.pincode = editFormData.pincode;
      } else if (editingField === 'address' && editFormData.address) {
        updateData.address = editFormData.address;
      }

      if (Object.keys(updateData).length === 0) {
        setMessageModal({ show: true, message: 'Please enter a value', type: 'warning', title: 'Validation Error' });
        setUpdating(false);
        return;
      }

      const response = await axios.put(Api.User.updateProfile, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setMessageModal({ show: true, message: 'Profile updated successfully!', type: 'success', title: 'Success' });
        setEditingField(null);
        // Update user context with merged data
        if (updateUser && response.data.user) {
          // Merge updated fields with existing user data to preserve all fields
          const updatedUser = {
            ...user, // Keep all existing user fields
            ...response.data.user // Override with updated fields from backend
          };
          updateUser(updatedUser);
        }
        // Don't reload - the state update will reflect changes immediately
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessageModal({ show: true, message: error.response?.data?.message || 'Failed to update profile. Please try again.', type: 'error', title: 'Error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }
      `}</style>
      <div className="bg-gray-50 pb-20 md:pb-0" style={{ paddingBottom: '80px' }}>
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="relative inline-block mb-4">
              <div 
                onClick={handleProfilePictureClick}
                className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center mx-auto cursor-pointer hover:ring-4 hover:ring-white/50 transition-all group relative overflow-hidden"
              >
                {profilePicturePreview ? (
                  <img 
                    src={profilePicturePreview} 
                    alt="Profile Preview" 
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : getProfileImageUrl(user?.profilePicture) ? (
                  <img 
                    src={getProfileImageUrl(user.profilePicture)} 
                    alt="Profile" 
                    className="w-full h-full object-cover rounded-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center"
                  style={{ display: (profilePicturePreview || getProfileImageUrl(user?.profilePicture)) ? 'none' : 'flex' }}
                >
                  <span className="text-2xl md:text-3xl font-bold text-white">
                    {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                {/* Edit overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                {uploadingPicture && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {user?.username || 'User'}
            </h1>
            <p className="text-lg text-primary-100">
              {user?.email || 'user@example.com'}
            </p>
            <p className="text-sm text-primary-200 mt-2">
              Click on profile picture to update
            </p>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-primary-600 mr-2 md:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Username
                </label>
                {editingField === 'username' ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={editFormData.username}
                      onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter username"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        {updating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-2.5 md:p-3 bg-gray-50 rounded-lg border flex items-center justify-between gap-2">
                    <span className="text-gray-900 text-sm md:text-base flex-1 min-w-0 truncate">{user?.username || 'Not provided'}</span>
                    <button
                      onClick={() => handleEditClick('username')}
                      className="p-1.5 md:p-1 text-gray-400 hover:text-primary-600 transition-colors flex-shrink-0"
                      title="Edit username"
                    >
                      <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Email Address
                </label>
                <div className="p-2.5 md:p-3 bg-gray-50 rounded-lg border flex items-center justify-between">
                  <span className="text-gray-900 text-sm md:text-base flex-1 min-w-0 truncate">{user?.email || 'Not provided'}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Phone Number
                </label>
                <div className="p-2.5 md:p-3 bg-gray-50 rounded-lg border flex items-center justify-between">
                  <span className="text-gray-900 text-sm md:text-base notranslate" translate="no">{user?.phone || 'Not provided'}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Pincode
                </label>
                {editingField === 'pincode' ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={editFormData.pincode}
                      onChange={(e) => setEditFormData({ ...editFormData, pincode: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter pincode"
                      maxLength="6"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        {updating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-2.5 md:p-3 bg-gray-50 rounded-lg border flex items-center justify-between gap-2">
                    <span className="text-gray-900 text-sm md:text-base flex-1 min-w-0 truncate">{user?.pincode || 'Not provided'}</span>
                    <button
                      onClick={() => handleEditClick('pincode')}
                      className="p-1.5 md:p-1 text-gray-400 hover:text-primary-600 transition-colors flex-shrink-0"
                      title="Edit pincode"
                    >
                      <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 md:mt-6">
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                Address
              </label>
              {editingField === 'address' ? (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2">
                  <textarea
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter address"
                    rows="3"
                  />
                  <div className="flex sm:flex-col gap-2">
                    <button
                      onClick={handleSaveEdit}
                      disabled={updating}
                      className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap"
                    >
                      {updating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={updating}
                      className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-2.5 md:p-3 bg-gray-50 rounded-lg border flex items-start justify-between gap-2">
                  <span className="text-gray-900 text-sm md:text-base flex-1 min-w-0 break-words">{user?.address || 'Not provided'}</span>
                  <button
                    onClick={() => handleEditClick('address')}
                    className="p-1.5 md:p-1 text-gray-400 hover:text-primary-600 transition-colors flex-shrink-0"
                    title="Edit address"
                  >
                    <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Account Status */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Account Status
            </h2>
            
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-medium text-green-800">Account Verified</p>
                  <p className="text-sm text-green-600">Your account is active and verified</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                Active
              </span>
            </div>
          </div>

          {/* Ride Statistics */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Ride Statistics
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                <div className="text-sm text-blue-800 font-medium">Total Rides</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
                <div className="text-sm text-green-800 font-medium">Completed</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-200">
                <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                <div className="text-sm text-yellow-800 font-medium">Pending</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200">
                <div className="text-2xl font-bold text-purple-600">₹{stats.totalSpent.toFixed(2)}</div>
                <div className="text-sm text-purple-800 font-medium">Total Spent</div>
              </div>
            </div>
          </div>


          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Actions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button className="flex items-center p-4 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors">
                <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-primary-800">Edit Profile</p>
                  <p className="text-sm text-primary-600">Update your information</p>
                </div>
              </button>
              
              <button className="flex items-center p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
                <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-green-800">Ride History</p>
                  <p className="text-sm text-green-600">View past rides</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Non-blocking Toast Notification - Appears automatically, disappears after 2.5s */}
      {messageModal.show && (
        <div 
          className="fixed w-[90%] max-w-md animate-slideDown"
          style={{ 
            zIndex: 999999,
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'auto'
          }}
          role="alert"
          aria-live="polite"
        >
          <div
            className={`px-5 py-4 rounded-xl shadow-2xl flex items-center gap-3 text-white border-2 ${
              messageModal.type === 'success'
                ? 'bg-green-600 border-green-400'
                : messageModal.type === 'error'
                ? 'bg-red-600 border-red-400'
                : messageModal.type === 'warning'
                ? 'bg-orange-600 border-orange-400'
                : 'bg-blue-600 border-blue-400'
            }`}
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
          >
            {/* Success Icon */}
            {messageModal.type === 'success' && (
              <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {/* Error Icon */}
            {messageModal.type === 'error' && (
              <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {/* Message Text */}
            <div className="flex-1">
              {messageModal.title && (
                <div className="text-base font-bold mb-1">{messageModal.title}</div>
              )}
              <div className="text-base font-semibold">{messageModal.message}</div>
            </div>
            {/* Optional Close Button (doesn't block, just allows manual dismiss) */}
            <button
              type="button"
              onClick={() => setMessageModal({ show: false, message: '', type: 'info', title: '' })}
              className="text-white/80 hover:text-white flex-shrink-0 ml-1 transition-opacity"
              aria-label="Close notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Message Modal - Only show for errors/warnings, not for success */}
      {messageModal.show && messageModal.type !== 'success' && (
        <MessageModal
          message={messageModal.message}
          title={messageModal.title}
          type={messageModal.type}
          onClose={() => setMessageModal({ show: false, message: '', type: 'info', title: 'Message' })}
        />
      )}
    </div>
    </>
  );
};

export default Profile;
